/**
 * PhonePopover Component
 * 
 * A dropdown/popover for phone contact options (Call / Text).
 * Styled to match the dark/gold theme.
 * - Closes on outside click or Escape key
 * - Keyboard accessible (Tab, Enter, Escape)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Phone, MessageSquare, X } from 'lucide-react';

const PHONE_NUMBER = '+19285306604';
const PHONE_DISPLAY = '(928) 530-6604';

const PhonePopover = ({ 
  variant = 'navbar', // 'navbar' | 'hero' | 'hero-outline'
  className = '',
  children 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click
  const handleClickOutside = useCallback((event) => {
    if (
      popoverRef.current && 
      !popoverRef.current.contains(event.target) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target)
    ) {
      setIsOpen(false);
    }
  }, []);

  // Close on Escape key
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClickOutside, handleKeyDown]);

  // Focus first option when opened
  useEffect(() => {
    if (isOpen && popoverRef.current) {
      const firstOption = popoverRef.current.querySelector('a');
      firstOption?.focus();
    }
  }, [isOpen]);

  const togglePopover = () => setIsOpen(!isOpen);

  // Handle keyboard navigation within popover
  const handlePopoverKeyDown = (event) => {
    const options = popoverRef.current?.querySelectorAll('a');
    if (!options) return;

    const currentIndex = Array.from(options).indexOf(document.activeElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % options.length;
      options[nextIndex]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = currentIndex === 0 ? options.length - 1 : currentIndex - 1;
      options[prevIndex]?.focus();
    } else if (event.key === 'Tab' && !event.shiftKey && currentIndex === options.length - 1) {
      setIsOpen(false);
    } else if (event.key === 'Tab' && event.shiftKey && currentIndex === 0) {
      setIsOpen(false);
    }
  };

  // Variant styles
  const getButtonStyles = () => {
    switch (variant) {
      case 'navbar':
        return 'flex items-center gap-2 text-[#E8E4DE] hover:text-[#C6A14A] transition-colors text-[13px] tracking-[0.05em]';
      case 'hero':
        return 'inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border border-white/30 text-white font-semibold text-lg tracking-wide hover:bg-white/10 transition-all duration-300';
      case 'hero-outline':
        return 'inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg border border-[#C6A14A]/30 text-[#C6A14A] font-semibold tracking-wide hover:bg-[#C6A14A]/10 hover:border-[#C6A14A]/50 transition-all duration-300';
      default:
        return '';
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={togglePopover}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            togglePopover();
          }
        }}
        className={getButtonStyles()}
        aria-expanded={isOpen}
        aria-haspopup="true"
        data-testid="phone-popover-trigger"
      >
        {children || (
          <>
            <Phone className="w-4 h-4" />
            {PHONE_DISPLAY}
          </>
        )}
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div
          ref={popoverRef}
          onKeyDown={handlePopoverKeyDown}
          className={`
            absolute z-50 mt-2 w-56 
            bg-[#0F1724] border border-[#C6A14A]/30 
            rounded-xl shadow-2xl overflow-hidden
            animate-in fade-in-0 zoom-in-95 duration-200
            ${variant === 'navbar' ? 'right-0' : 'left-1/2 -translate-x-1/2'}
          `}
          role="menu"
          aria-orientation="vertical"
          data-testid="phone-popover-menu"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#C6A14A]/20 flex items-center justify-between">
            <span className="text-white/80 text-sm font-medium">Contact Options</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Options */}
          <div className="py-2">
            {/* Call Option */}
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="flex items-center gap-3 px-4 py-3 text-white hover:bg-[#C6A14A]/20 transition-colors focus:bg-[#C6A14A]/20 focus:outline-none"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              data-testid="phone-call-option"
            >
              <div className="w-10 h-10 rounded-full bg-[#C6A14A]/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#C6A14A]" />
              </div>
              <div>
                <p className="font-medium text-white">Call</p>
                <p className="text-xs text-white/60">{PHONE_DISPLAY}</p>
              </div>
            </a>

            {/* Text / Message Option */}
            <a
              href={`sms:${PHONE_NUMBER}`}
              className="flex items-center gap-3 px-4 py-3 text-white hover:bg-[#C6A14A]/20 transition-colors focus:bg-[#C6A14A]/20 focus:outline-none"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              data-testid="phone-text-option"
            >
              <div className="w-10 h-10 rounded-full bg-[#C6A14A]/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#C6A14A]" />
              </div>
              <div>
                <p className="font-medium text-white">Text / Message</p>
                <p className="text-xs text-white/60">Send an SMS</p>
              </div>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhonePopover;
